import React from 'react';
import { NavLink } from 'react-router-dom';
import { Shield, Lock, Server, FileText, CheckCircle2, ArrowLeft } from 'lucide-react';

const Security: React.FC = () => {
    return (
        <div className="min-h-screen bg-background-dark text-white font-sans selection:bg-primary/30">
            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 bg-background-dark/80 backdrop-blur-md border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <NavLink to="/" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> Volver
                        </NavLink>
                        <div className="h-6 w-px bg-white/10"></div>
                        <img src="/logo.png" alt="FlowSpec QA" className="h-8 w-auto opacity-80" />
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-40 pb-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold mb-6 tracking-widest uppercase">
                        <Shield className="w-4 h-4" />
                        Security Whitepaper
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black mb-8 tracking-tight">
                        Arquitectura de <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">Seguridad Primero.</span>
                    </h1>
                    <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
                        En FlowSpec QA, la seguridad no es una característica opcional, es el cimiento de nuestra ingeniería.
                        Este documento detalla cómo protegemos sus activos de prueba y datos sensibles.
                    </p>
                </div>
            </section>

            {/* Core Pillars */}
            <section className="py-20 px-4 border-y border-white/5 bg-white/[0.02]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
                    {[
                        {
                            icon: <Lock className="w-8 h-8 text-emerald-400" />,
                            title: "Encriptación de Grado Militar",
                            desc: "Todos los datos sensibles (API Keys, Credenciales) se cifran en reposo usando Fernet (AES-128) con rotación de claves. El tráfico en tránsito está protegido exclusivamente por TLS 1.3."
                        },
                        {
                            icon: <Server className="w-8 h-8 text-blue-400" />,
                            title: "Aislamiento de Inquilinos",
                            desc: "Implementamos una arquitectura Multi-Tenant con aislamiento lógico estricto. El Middleware 'TenantMiddleware' garantiza que ninguna organización pueda acceder a los datos de otra, ni siquiera por error de consulta."
                        },
                        {
                            icon: <FileText className="w-8 h-8 text-purple-400" />,
                            title: "Privacidad RAG & AI",
                            desc: "Nuestra arquitectura RAG (Retrieval-Augmented Generation) procesa sus documentos en memoria efímera. Sus datos NUNCA se utilizan para entrenar los modelos fundacionales de OpenAI o Anthropic."
                        }
                    ].map((item, i) => (
                        <div key={i} className="p-8 rounded-3xl bg-background-dark border border-white/5 hover:border-white/10 transition-colors">
                            <div className="mb-6 bg-white/5 w-16 h-16 rounded-2xl flex items-center justify-center">
                                {item.icon}
                            </div>
                            <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                            <p className="text-slate-400 leading-relaxed text-sm">
                                {item.desc}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* DetailedSpecs */}
            <section className="py-20 px-4">
                <div className="max-w-5xl mx-auto">
                    <h2 className="text-3xl font-bold mb-12">Especificaciones Técnicas</h2>

                    <div className="space-y-8">
                        {/* Auth */}
                        <div className="flex gap-6 items-start p-6 rounded-2xl bg-white/5 border border-white/5">
                            <div className="shrink-0 mt-1"><CheckCircle2 className="w-6 h-6 text-emerald-500" /></div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">Autenticación y Sesiones</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Utilizamos <strong>PyJWT</strong> (Estándar de la industria) con tokens de vida corta (Access Tokens) y hashing de contraseñas mediante <strong>Bcrypt</strong> con sal y rondas adaptativas. No almacenamos contraseñas en texto plano bajo ninguna circunstancia.
                                </p>
                            </div>
                        </div>

                        {/* Infrastructure */}
                        <div className="flex gap-6 items-start p-6 rounded-2xl bg-white/5 border border-white/5">
                            <div className="shrink-0 mt-1"><CheckCircle2 className="w-6 h-6 text-emerald-500" /></div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">Infraestructura Inmutable via Coolify</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    Nuestros despliegues son atómicos y contenerizados (Docker). Cada versión del software es inmutable, eliminando la "desviación de configuración". Los secretos de entorno se inyectan en tiempo de ejecución y nunca se commitean al repositorio.
                                </p>
                            </div>
                        </div>

                        {/* Audit */}
                        <div className="flex gap-6 items-start p-6 rounded-2xl bg-white/5 border border-white/5">
                            <div className="shrink-0 mt-1"><CheckCircle2 className="w-6 h-6 text-emerald-500" /></div>
                            <div>
                                <h3 className="text-lg font-bold mb-2">Sanitización de Archivos</h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    El módulo de subida de archivos (Ingestion) utiliza validación de "Magic Bytes" (python-magic) para prevenir la ejecución de malware disfrazado. Todos los archivos se renombran a UUIDv4 para prevenir ataques de Path Traversal.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 text-center text-slate-600 text-sm border-t border-white/5">
                <p>© 2026 IAFlows. FlowSpec QA. Security Engineering Team.</p>
            </footer>
        </div>
    );
};

export default Security;
